/**
 * @engine/physics2d - Physics World
 *
 * Central simulation coordinator. Manages bodies, colliders,
 * and runs the full physics pipeline each fixed-update:
 *
 * 1. Apply forces + gravity
 * 2. Integrate velocities → tentative positions
 * 3. Update AABBs
 * 4. Broad-phase (spatial hash)
 * 5. Narrow-phase (SAT/specialized)
 * 6. Solve velocity constraints (sequential impulse)
 * 7. Integrate positions
 * 8. Solve position constraints (Baumgarte)
 * 9. Sync transforms back to GameObjects
 * 10. Fire collision/trigger events
 * 11. Sleep management
 */

import type {
  PhysicsBodyState,
  ColliderState,
  PhysVec2,
  CollisionManifold,
  PhysicsWorldConfig,
  RaycastHit2D,
  CollisionEvent2D,
  TriggerEvent2D,
  PhysicsMaterial2D,
  MassData,
  GravityZoneConfig,
} from '../types';
import {
  BodyType,
  SleepState,
  ColliderType,
  CapsuleDirection,
  GravityZoneType,
  GravityZoneShape,
  GravityFalloff,
  SOLVER_DEFAULTS,
  PhysicsLayers,
  computeColliderAABB,
  defaultMaterial,
} from '../types';
import { SpatialHash } from '../collision/BroadPhase';
import { testCollision } from '../collision/NarrowPhase';
import { solveVelocities, solvePositions, type VelocityConstraint } from './Solver';
import { raycast, raycastAll, overlapPoint, overlapBox, overlapCircle } from '../collision/Raycast2D';
import { Profiler } from '@engine/core';

// ============================================================================
// EVENT CALLBACKS
// ============================================================================

export type CollisionCallback = (event: CollisionEvent2D) => void;
export type TriggerCallback = (event: TriggerEvent2D) => void;

export interface PhysicsEventHandlers {
  onCollisionEnter?: CollisionCallback;
  onCollisionStay?: CollisionCallback;
  onCollisionExit?: CollisionCallback;
  onTriggerEnter?: TriggerCallback;
  onTriggerStay?: TriggerCallback;
  onTriggerExit?: TriggerCallback;
}

// ============================================================================
// GRAVITY ZONE STATE
// ============================================================================

/** Runtime state for an active gravity zone */
export interface GravityZoneState {
  /** Unique zone ID */
  zoneId: number;
  /** GUID of the GameObject hosting this zone */
  guid: string;
  /** Zone center position (world space) */
  position: PhysVec2;
  /** Zone rotation in radians */
  rotation: number;
  /** Zone scale */
  scale: PhysVec2;
  /** Zone configuration */
  config: GravityZoneConfig;
}

// ============================================================================
// BUOYANCY ZONE STATE
// ============================================================================

/** Runtime state for a buoyancy zone */
export interface BuoyancyZoneState {
  zoneId: number;
  guid: string;
  position: PhysVec2;
  rotation: number;
  scale: PhysVec2;
  density: number;
  linearDrag: number;
  angularDrag: number;
  flow: PhysVec2;
  surfaceNormal: PhysVec2;
  width: number;
  height: number;
  priority: number;
  affectedLayers: number;
}

// ============================================================================
// WIND ZONE STATE
// ============================================================================

/** Runtime state for a wind zone */
export interface WindZoneState {
  zoneId: number;
  guid: string;
  position: PhysVec2;
  rotation: number;
  scale: PhysVec2;
  mode: number;
  direction: PhysVec2;
  strength: number;
  turbulence: number;
  turbulenceFrequency: number;
  pulse: number;
  pulseFrequency: number;
  shape: number;
  radius: number;
  width: number;
  height: number;
  falloff: number;
  falloffStart: number;
  priority: number;
  affectedLayers: number;
}

// ============================================================================
// PHYSICS WORLD
// ============================================================================

export class PhysicsWorld {
  // Config
  private _gravity: PhysVec2;
  private _fixedTimestep: number;
  private _velocityIterations: number;
  private _positionIterations: number;
  private _allowSleeping: boolean;

  // Bodies & colliders (SoA-style arrays, index = ID)
  private _bodies: PhysicsBodyState[] = [];
  private _colliders: ColliderState[] = [];
  private _bodyMap: Map<string, number> = new Map(); // guid → bodyIndex
  private _nextBodyIndex = 0;
  private _nextColliderIndex = 0;

  // Gravity zones
  private _gravityZones: GravityZoneState[] = [];
  private _zoneMap: Map<string, number> = new Map(); // guid → zoneIndex
  private _nextZoneIndex = 0;

  // Buoyancy zones
  private _buoyancyZones: BuoyancyZoneState[] = [];
  private _buoyancyMap: Map<string, number> = new Map();
  private _nextBuoyancyIndex = 0;

  // Wind zones
  private _windZones: WindZoneState[] = [];
  private _windMap: Map<string, number> = new Map();
  private _nextWindIndex = 0;

  // Time accumulator for turbulence/pulse
  private _time = 0;

  // Broad-phase
  private _broadPhase: SpatialHash;

  // Contact state (for enter/stay/exit tracking)
  private _activeContacts: Map<string, CollisionManifold> = new Map();
  private _activeTriggers: Set<string> = new Set();

  // Event handlers per body
  private _eventHandlers: Map<number, PhysicsEventHandlers> = new Map();

  // Timing
  private _accumulator = 0;
  private _stepCount = 0;

  constructor(config?: Partial<PhysicsWorldConfig>) {
    this._gravity = config?.gravity ?? { ...SOLVER_DEFAULTS.gravity };
    this._fixedTimestep = config?.fixedTimestep ?? SOLVER_DEFAULTS.fixedTimestep;
    this._velocityIterations = config?.velocityIterations ?? SOLVER_DEFAULTS.velocityIterations;
    this._positionIterations = config?.positionIterations ?? SOLVER_DEFAULTS.positionIterations;
    this._allowSleeping = config?.allowSleeping ?? true;
    this._broadPhase = new SpatialHash(config?.broadPhaseGrid ?? 4.0);
  }

  // ==========================================================================
  // CONFIG
  // ==========================================================================

  get gravity(): PhysVec2 { return this._gravity; }
  set gravity(v: PhysVec2) { this._gravity = { x: v.x, y: v.y }; }

  get fixedTimestep(): number { return this._fixedTimestep; }
  get stepCount(): number { return this._stepCount; }
  get bodyCount(): number { return this._bodies.length; }
  get colliderCount(): number { return this._colliders.length; }

  /**
   * Get diagnostics for profiling.
   */
  getDiagnostics(): {
    bodyCount: number;
    colliderCount: number;
    activeBodies: number;
    sleepingBodies: number;
    broadPhasePairs: number;
    narrowPhaseContacts: number;
    islandCount: number;
    stepCount: number;
  } {
    let activeBodies = 0;
    let sleepingBodies = 0;
    
    for (const body of this._bodies) {
      if (!body) continue;
      if (body.sleepState === SleepState.Sleeping) {
        sleepingBodies++;
      } else if (body.bodyType === BodyType.Dynamic) {
        activeBodies++;
      }
    }
    
    return {
      bodyCount: this._bodies.length,
      colliderCount: this._colliders.length,
      activeBodies,
      sleepingBodies,
      broadPhasePairs: this._lastBroadPhasePairs,
      narrowPhaseContacts: this._lastNarrowPhaseContacts,
      islandCount: 0, // Built-in doesn't use islands
      stepCount: this._stepCount,
    };
  }

  // Diagnostics tracking
  private _lastBroadPhasePairs = 0;
  private _lastNarrowPhaseContacts = 0;

  // ==========================================================================
  // BODY MANAGEMENT
  // ==========================================================================

  /**
   * Create a physics body. Returns the body index.
   */
  createBody(
    guid: string,
    gameObjectId: number,
    bodyType: BodyType = BodyType.Dynamic,
    position: PhysVec2 = { x: 0, y: 0 },
    rotation: number = 0,
  ): number {
    const index = this._nextBodyIndex++;

    const body: PhysicsBodyState = {
      bodyIndex: index,
      gameObjectId,
      guid,
      bodyType,
      sleepState: SleepState.Awake,
      position: { x: position.x, y: position.y },
      rotation,
      linearVelocity: { x: 0, y: 0 },
      angularVelocity: 0,
      mass: bodyType === BodyType.Dynamic ? 1 : 0,
      invMass: bodyType === BodyType.Dynamic ? 1 : 0,
      inertia: bodyType === BodyType.Dynamic ? 1 : 0,
      invInertia: bodyType === BodyType.Dynamic ? 1 : 0,
      centerOfMass: { x: 0, y: 0 },
      density: 1,
      linearDamping: 0,
      angularDamping: 0.05,
      gravityScale: 1,
      constraints: 0,
      collisionDetection: 0,
      interpolation: 0,
      layerMask: PhysicsLayers.Default,
      sleepTimer: 0,
      forceAccum: { x: 0, y: 0 },
      torqueAccum: 0,
    };

    this._bodies[index] = body;
    this._bodyMap.set(guid, index);
    return index;
  }

  /**
   * Remove a body and all its colliders.
   */
  removeBody(bodyIndex: number): void {
    const body = this._bodies[bodyIndex];
    if (!body) return;

    // Remove all colliders attached to this body
    for (let i = this._colliders.length - 1; i >= 0; i--) {
      if (this._colliders[i]?.bodyIndex === bodyIndex) {
        this._colliders.splice(i, 1);
      }
    }

    // Purge stale contact tracking so removed bodies never fire exit events
    const prefix1 = `${bodyIndex}:`;
    const suffix1 = `:${bodyIndex}`;
    for (const key of this._activeContacts.keys()) {
      if (key.startsWith(prefix1) || key.endsWith(suffix1)) {
        this._activeContacts.delete(key);
      }
    }

    this._bodyMap.delete(body.guid);
    delete this._bodies[bodyIndex];
    this._eventHandlers.delete(bodyIndex);
  }

  getBody(bodyIndex: number): PhysicsBodyState | undefined {
    return this._bodies[bodyIndex];
  }

  getBodyByGuid(guid: string): PhysicsBodyState | undefined {
    const idx = this._bodyMap.get(guid);
    return idx !== undefined ? this._bodies[idx] : undefined;
  }

  /**
   * Iterate all active bodies. Callback receives the body state.
   */
  forEachBody(fn: (body: PhysicsBodyState) => void): void {
    for (const body of this._bodies) {
      if (body) fn(body);
    }
  }

  // ==========================================================================
  // COLLIDER MANAGEMENT
  // ==========================================================================

  /**
   * Add a collider to a body. Returns collider index.
   */
  addCollider(bodyIndex: number, collider: Partial<ColliderState>): number {
    const index = this._nextColliderIndex++;
    const mat = collider.material ?? defaultMaterial();

    const state: ColliderState = {
      colliderIndex: index,
      bodyIndex,
      gameObjectId: this._bodies[bodyIndex]?.gameObjectId ?? 0,
      type: collider.type ?? ColliderType.Box,
      isTrigger: collider.isTrigger ?? false,
      offset: collider.offset ?? { x: 0, y: 0 },
      rotation: collider.rotation ?? 0,
      halfExtents: collider.halfExtents ?? { x: 0.5, y: 0.5 },
      radius: collider.radius ?? 0.5,
      capsuleHeight: collider.capsuleHeight ?? 1,
      capsuleDirection: collider.capsuleDirection ?? CapsuleDirection.Vertical,
      vertices: collider.vertices ?? [],
      normals: collider.normals ?? [],
      vertexCount: collider.vertexCount ?? 0,
      material: { ...mat },
      layerMask: collider.layerMask ?? PhysicsLayers.Default,
      collisionMask: collider.collisionMask ?? PhysicsLayers.All,
      oneWay: collider.oneWay ?? false,
      oneWayDirection: collider.oneWayDirection ?? { x: 0, y: 1 },
      aabbMin: { x: 0, y: 0 },
      aabbMax: { x: 0, y: 0 },
    };

    this._colliders[index] = state;

    // Recompute mass if dynamic
    this._recomputeBodyMass(bodyIndex);

    return index;
  }

  removeCollider(colliderIndex: number): void {
    const collider = this._colliders[colliderIndex];
    if (!collider) return;
    const bodyIdx = collider.bodyIndex;
    delete this._colliders[colliderIndex];
    this._recomputeBodyMass(bodyIdx);
  }

  getCollider(colliderIndex: number): ColliderState | undefined {
    return this._colliders[colliderIndex];
  }

  // ==========================================================================
  // GRAVITY ZONES
  // ==========================================================================

  /**
   * Add a gravity zone. Returns zone ID.
   */
  addGravityZone(guid: string, position: PhysVec2, rotation: number, scale: PhysVec2, config: GravityZoneConfig): number {
    const id = this._nextZoneIndex++;
    const gravity = config.gravity ?? { x: 0, y: 0 };
    const size = config.size ?? { x: 4, y: 2 };
    const zone: GravityZoneState = {
      zoneId: id,
      guid,
      position: { x: position.x, y: position.y },
      rotation,
      scale: { x: scale.x, y: scale.y },
      config: {
        ...config,
        gravity: { x: gravity.x, y: gravity.y },
        size: { x: size.x, y: size.y },
        points: config.points ? [...config.points.map(p => ({ ...p }))] : [],
      },
    };
    this._gravityZones[id] = zone;
    this._zoneMap.set(guid, id);
    return id;
  }

  /**
   * Update gravity zone transform (called when GameObject moves/rotates/scales).
   */
  updateGravityZonePosition(guid: string, position: PhysVec2, rotation?: number, scale?: PhysVec2): void {
    const id = this._zoneMap.get(guid);
    if (id === undefined) return;
    const zone = this._gravityZones[id];
    if (zone) {
      zone.position.x = position.x;
      zone.position.y = position.y;
      if (rotation !== undefined) zone.rotation = rotation;
      if (scale !== undefined) {
        zone.scale.x = scale.x;
        zone.scale.y = scale.y;
      }
    }
  }

  /**
   * Update gravity zone config (called when component changes).
   */
  updateGravityZoneConfig(guid: string, config: Partial<GravityZoneConfig>): void {
    const id = this._zoneMap.get(guid);
    if (id === undefined) return;
    const zone = this._gravityZones[id];
    if (zone) {
      Object.assign(zone.config, config);
      if (config.gravity) {
        zone.config.gravity = { ...config.gravity };
      }
      if (config.size) {
        zone.config.size = { ...config.size };
      }
      if (config.points) {
        zone.config.points = [...config.points.map(p => ({ ...p }))];
      }
    }
  }

  /**
   * Remove a gravity zone.
   */
  removeGravityZone(guid: string): void {
    const id = this._zoneMap.get(guid);
    if (id === undefined) return;
    delete this._gravityZones[id];
    this._zoneMap.delete(guid);
  }

  /**
   * Get gravity zone by GUID.
   */
  getGravityZone(guid: string): GravityZoneState | undefined {
    const id = this._zoneMap.get(guid);
    return id !== undefined ? this._gravityZones[id] : undefined;
  }

  get gravityZoneCount(): number {
    return this._gravityZones.filter(z => z).length;
  }

  // ==========================================================================
  // BUOYANCY ZONES
  // ==========================================================================

  addBuoyancyZone(guid: string, position: PhysVec2, rotation: number, scale: PhysVec2, config: {
    density: number;
    linearDrag: number;
    angularDrag: number;
    flow: PhysVec2;
    surfaceNormal: PhysVec2;
    width: number;
    height: number;
    priority: number;
    affectedLayers: number;
  }): number {
    const id = this._nextBuoyancyIndex++;
    const zone: BuoyancyZoneState = {
      zoneId: id,
      guid,
      position: { x: position.x, y: position.y },
      rotation,
      scale: { x: scale.x, y: scale.y },
      density: config.density,
      linearDrag: config.linearDrag,
      angularDrag: config.angularDrag,
      flow: { x: config.flow.x, y: config.flow.y },
      surfaceNormal: { x: config.surfaceNormal.x, y: config.surfaceNormal.y },
      width: config.width,
      height: config.height,
      priority: config.priority,
      affectedLayers: config.affectedLayers,
    };
    this._buoyancyZones[id] = zone;
    this._buoyancyMap.set(guid, id);
    return id;
  }

  updateBuoyancyZonePosition(guid: string, position: PhysVec2, rotation?: number, scale?: PhysVec2): void {
    const id = this._buoyancyMap.get(guid);
    if (id === undefined) return;
    const zone = this._buoyancyZones[id];
    if (zone) {
      zone.position.x = position.x;
      zone.position.y = position.y;
      if (rotation !== undefined) zone.rotation = rotation;
      if (scale !== undefined) {
        zone.scale.x = scale.x;
        zone.scale.y = scale.y;
      }
    }
  }

  updateBuoyancyZoneConfig(guid: string, config: Partial<BuoyancyZoneState>): void {
    const id = this._buoyancyMap.get(guid);
    if (id === undefined) return;
    const zone = this._buoyancyZones[id];
    if (zone) {
      if (config.density !== undefined) zone.density = config.density;
      if (config.linearDrag !== undefined) zone.linearDrag = config.linearDrag;
      if (config.angularDrag !== undefined) zone.angularDrag = config.angularDrag;
      if (config.flow) zone.flow = { ...config.flow };
      if (config.surfaceNormal) zone.surfaceNormal = { ...config.surfaceNormal };
      if (config.width !== undefined) zone.width = config.width;
      if (config.height !== undefined) zone.height = config.height;
      if (config.priority !== undefined) zone.priority = config.priority;
      if (config.affectedLayers !== undefined) zone.affectedLayers = config.affectedLayers;
    }
  }

  removeBuoyancyZone(guid: string): void {
    const id = this._buoyancyMap.get(guid);
    if (id === undefined) return;
    delete this._buoyancyZones[id];
    this._buoyancyMap.delete(guid);
  }

  // ==========================================================================
  // WIND ZONES
  // ==========================================================================

  addWindZone(guid: string, position: PhysVec2, rotation: number, scale: PhysVec2, config: {
    mode: number;
    direction: PhysVec2;
    strength: number;
    turbulence: number;
    turbulenceFrequency: number;
    pulse: number;
    pulseFrequency: number;
    shape: number;
    radius: number;
    width: number;
    height: number;
    falloff: number;
    falloffStart: number;
    priority: number;
    affectedLayers: number;
  }): number {
    const id = this._nextWindIndex++;
    const zone: WindZoneState = {
      zoneId: id,
      guid,
      position: { x: position.x, y: position.y },
      rotation,
      scale: { x: scale.x, y: scale.y },
      mode: config.mode,
      direction: { x: config.direction.x, y: config.direction.y },
      strength: config.strength,
      turbulence: config.turbulence,
      turbulenceFrequency: config.turbulenceFrequency,
      pulse: config.pulse,
      pulseFrequency: config.pulseFrequency,
      shape: config.shape,
      radius: config.radius,
      width: config.width,
      height: config.height,
      falloff: config.falloff,
      falloffStart: config.falloffStart,
      priority: config.priority,
      affectedLayers: config.affectedLayers,
    };
    this._windZones[id] = zone;
    this._windMap.set(guid, id);
    return id;
  }

  updateWindZonePosition(guid: string, position: PhysVec2, rotation?: number, scale?: PhysVec2): void {
    const id = this._windMap.get(guid);
    if (id === undefined) return;
    const zone = this._windZones[id];
    if (zone) {
      zone.position.x = position.x;
      zone.position.y = position.y;
      if (rotation !== undefined) zone.rotation = rotation;
      if (scale !== undefined) {
        zone.scale.x = scale.x;
        zone.scale.y = scale.y;
      }
    }
  }

  updateWindZoneConfig(guid: string, config: Partial<WindZoneState>): void {
    const id = this._windMap.get(guid);
    if (id === undefined) return;
    const zone = this._windZones[id];
    if (zone) {
      if (config.mode !== undefined) zone.mode = config.mode;
      if (config.direction) zone.direction = { ...config.direction };
      if (config.strength !== undefined) zone.strength = config.strength;
      if (config.turbulence !== undefined) zone.turbulence = config.turbulence;
      if (config.turbulenceFrequency !== undefined) zone.turbulenceFrequency = config.turbulenceFrequency;
      if (config.pulse !== undefined) zone.pulse = config.pulse;
      if (config.pulseFrequency !== undefined) zone.pulseFrequency = config.pulseFrequency;
      if (config.shape !== undefined) zone.shape = config.shape;
      if (config.radius !== undefined) zone.radius = config.radius;
      if (config.width !== undefined) zone.width = config.width;
      if (config.height !== undefined) zone.height = config.height;
      if (config.falloff !== undefined) zone.falloff = config.falloff;
      if (config.falloffStart !== undefined) zone.falloffStart = config.falloffStart;
      if (config.priority !== undefined) zone.priority = config.priority;
      if (config.affectedLayers !== undefined) zone.affectedLayers = config.affectedLayers;
    }
  }

  removeWindZone(guid: string): void {
    const id = this._windMap.get(guid);
    if (id === undefined) return;
    delete this._windZones[id];
    this._windMap.delete(guid);
  }

  // ==========================================================================
  // FORCES
  // ==========================================================================

  applyForce(bodyIndex: number, force: PhysVec2): void {
    const body = this._bodies[bodyIndex];
    if (!body || body.bodyType !== BodyType.Dynamic) return;
    body.forceAccum.x += force.x;
    body.forceAccum.y += force.y;
    this._wakeBody(body);
  }

  applyImpulse(bodyIndex: number, impulse: PhysVec2): void {
    const body = this._bodies[bodyIndex];
    if (!body || body.bodyType !== BodyType.Dynamic) return;
    body.linearVelocity.x += impulse.x * body.invMass;
    body.linearVelocity.y += impulse.y * body.invMass;
    this._wakeBody(body);
  }

  applyTorque(bodyIndex: number, torque: number): void {
    const body = this._bodies[bodyIndex];
    if (!body || body.bodyType !== BodyType.Dynamic) return;
    body.torqueAccum += torque;
    this._wakeBody(body);
  }

  applyAngularImpulse(bodyIndex: number, impulse: number): void {
    const body = this._bodies[bodyIndex];
    if (!body || body.bodyType !== BodyType.Dynamic) return;
    body.angularVelocity += impulse * body.invInertia;
    this._wakeBody(body);
  }

  // ==========================================================================
  // EVENTS
  // ==========================================================================

  setEventHandlers(bodyIndex: number, handlers: PhysicsEventHandlers): void {
    this._eventHandlers.set(bodyIndex, handlers);
  }

  // ==========================================================================
  // QUERIES
  // ==========================================================================

  raycast(origin: PhysVec2, direction: PhysVec2, maxDistance: number, layerMask?: number): RaycastHit2D | null {
    return raycast(origin, direction, maxDistance, this._bodies, this._colliders, this._broadPhase, layerMask);
  }

  raycastAll(origin: PhysVec2, direction: PhysVec2, maxDistance: number, layerMask?: number): RaycastHit2D[] {
    return raycastAll(origin, direction, maxDistance, this._bodies, this._colliders, this._broadPhase, layerMask);
  }

  overlapPoint(point: PhysVec2, layerMask?: number): number[] {
    return overlapPoint(point, this._bodies, this._colliders, this._broadPhase, layerMask);
  }

  overlapBox(center: PhysVec2, halfExtents: PhysVec2, layerMask?: number): number[] {
    return overlapBox(center, halfExtents, this._bodies, this._colliders, this._broadPhase, layerMask);
  }

  overlapCircle(center: PhysVec2, radius: number, layerMask?: number): number[] {
    return overlapCircle(center, radius, this._bodies, this._colliders, this._broadPhase, layerMask);
  }

  /** Force a body to sleep. */
  sleepBody(bodyIndex: number): void {
    const body = this._bodies[bodyIndex];
    if (!body || body.bodyType === BodyType.Static) return;
    body.sleepState = SleepState.Sleeping;
    body.linearVelocity.x = 0;
    body.linearVelocity.y = 0;
    body.angularVelocity = 0;
    body.sleepTimer = 0;
  }

  /** Wake a sleeping body. */
  wakeBody(bodyIndex: number): void {
    const body = this._bodies[bodyIndex];
    if (body) this._wakeBody(body);
  }

  // ==========================================================================
  // SIMULATION STEP
  // ==========================================================================

  /**
   * Advance simulation by dt seconds (accumulator-based fixed timestep).
   */
  update(dt: number): void {
    this._accumulator += dt;

    // Cap accumulator to prevent spiral of death
    if (this._accumulator > this._fixedTimestep * 8) {
      this._accumulator = this._fixedTimestep * 8;
    }

    while (this._accumulator >= this._fixedTimestep) {
      this._step(this._fixedTimestep);
      this._accumulator -= this._fixedTimestep;
      this._stepCount++;
    }
  }

  /**
   * Single fixed-timestep simulation step.
   */
  private _step(dt: number): void {
    const invDt = dt > 0 ? 1 / dt : 0;

    // Update time for turbulence/pulse calculations
    this._time += dt;

    // 0. Apply effector forces (buoyancy, wind)
    Profiler.begin('Physics.Effectors');
    this._applyEffectorForces(dt);
    Profiler.end('Physics.Effectors');

    // 1. Apply gravity + integrate forces → velocities
    Profiler.begin('Physics.IntegrateForces');
    this._integrateForces(dt);
    Profiler.end('Physics.IntegrateForces');

    // 2. Update AABBs
    Profiler.begin('Physics.UpdateAABBs');
    this._updateAABBs();
    Profiler.end('Physics.UpdateAABBs');

    // 3. Broad-phase
    Profiler.begin('Physics.BroadPhase');
    this._broadPhase.clear();
    for (const col of this._colliders) {
      if (!col) continue;
      this._broadPhase.insert(col.colliderIndex, {
        minX: col.aabbMin.x,
        minY: col.aabbMin.y,
        maxX: col.aabbMax.x,
        maxY: col.aabbMax.y,
      });
    }
    const pairs = this._broadPhase.queryPairs();
    this._lastBroadPhasePairs = pairs.length;
    Profiler.end('Physics.BroadPhase');

    // 4. Narrow-phase
    Profiler.begin('Physics.NarrowPhase');
    const manifolds: CollisionManifold[] = [];
    const triggerPairs: { a: ColliderState; b: ColliderState }[] = [];

    for (const pair of pairs) {
      const colA = this._colliders[pair.indexA];
      const colB = this._colliders[pair.indexB];
      if (!colA || !colB) continue;

      // Layer filter
      if ((colA.layerMask & colB.collisionMask) === 0 &&
          (colB.layerMask & colA.collisionMask) === 0) continue;

      // Same body check
      if (colA.bodyIndex === colB.bodyIndex) continue;

      const bodyA = this._bodies[colA.bodyIndex];
      const bodyB = this._bodies[colB.bodyIndex];
      if (!bodyA || !bodyB) continue;

      // Skip static-static
      if (bodyA.bodyType === BodyType.Static && bodyB.bodyType === BodyType.Static) continue;

      // Both sleeping → skip
      if (bodyA.sleepState === SleepState.Sleeping && bodyB.sleepState === SleepState.Sleeping) continue;

      const manifold = testCollision(
        colA, colB,
        bodyA.position, bodyA.rotation,
        bodyB.position, bodyB.rotation,
      );

      if (manifold) {
        if (colA.isTrigger || colB.isTrigger) {
          triggerPairs.push({ a: colA, b: colB });
        } else {
          // One-way platform filtering: skip contact if body is on the pass-through side
          if (colA.oneWay || colB.oneWay) {
            const owCol = colA.oneWay ? colA : colB;
            const otherBody = colA.oneWay ? bodyB : bodyA;
            const owBody = colA.oneWay ? bodyA : bodyB;

            // Relative position: other body center minus platform center
            const relX = otherBody.position.x - owBody.position.x;
            const relY = otherBody.position.y - owBody.position.y;
            const dir = owCol.oneWayDirection;

            // posDot > 0 means the other body is on the solid side
            const posDot = relX * dir.x + relY * dir.y;

            // velDot < 0 means the other body is moving toward the solid side
            const velDot = otherBody.linearVelocity.x * dir.x + otherBody.linearVelocity.y * dir.y;

            // Allow pass-through when on the non-solid side or moving away
            if (posDot < 0.01 || velDot > 0) continue;
          }

          manifolds.push(manifold);
          // Wake bodies on contact
          this._wakeBody(bodyA);
          this._wakeBody(bodyB);
        }
      }
    }
    this._lastNarrowPhaseContacts = manifolds.length;
    Profiler.end('Physics.NarrowPhase');

    // 5. Solve velocity constraints
    Profiler.begin('Physics.SolveVelocity');
    let velocityConstraints: VelocityConstraint[] = [];
    if (manifolds.length > 0) {
      velocityConstraints = solveVelocities(manifolds, this._bodies, this._velocityIterations, dt);
    }
    Profiler.end('Physics.SolveVelocity');

    // 6. Integrate positions
    Profiler.begin('Physics.IntegratePositions');
    this._integratePositions(dt);
    Profiler.end('Physics.IntegratePositions');

    // 7. Solve position constraints (uses local anchors to recompute separation)
    Profiler.begin('Physics.SolvePosition');
    if (velocityConstraints.length > 0) {
      solvePositions(velocityConstraints, this._bodies, this._positionIterations);
    }
    Profiler.end('Physics.SolvePosition');

    // 8. Clamp velocities
    this._clampVelocities();

    // 9. Sleep management
    if (this._allowSleeping) {
      this._updateSleep(dt);
    }

    // 10. Fire events
    this._fireCollisionEvents(manifolds);
    this._fireTriggerEvents(triggerPairs);

    // 11. Clear forces
    for (const body of this._bodies) {
      if (!body) continue;
      body.forceAccum.x = 0;
      body.forceAccum.y = 0;
      body.torqueAccum = 0;
    }
  }

  // ==========================================================================
  // INTEGRATION
  // ==========================================================================

  /**
   * Transform a world point to local zone space (accounting for position, rotation, scale).
   */
  private _worldToZoneLocal(wx: number, wy: number, zone: GravityZoneState): { x: number; y: number } {
    // Translate to zone origin
    const tx = wx - zone.position.x;
    const ty = wy - zone.position.y;
    
    // Rotate by -rotation
    const cos = Math.cos(-zone.rotation);
    const sin = Math.sin(-zone.rotation);
    const rx = tx * cos - ty * sin;
    const ry = tx * sin + ty * cos;
    
    // Scale (inverse)
    const sx = zone.scale.x !== 0 ? rx / zone.scale.x : rx;
    const sy = zone.scale.y !== 0 ? ry / zone.scale.y : ry;
    
    return { x: sx, y: sy };
  }

  /**
   * Compute signed distance to box (negative inside, positive outside).
   */
  private _signedDistanceBox(lx: number, ly: number, halfW: number, halfH: number): number {
    const dx = Math.abs(lx) - halfW;
    const dy = Math.abs(ly) - halfH;
    const outsideDist = Math.sqrt(Math.max(dx, 0) ** 2 + Math.max(dy, 0) ** 2);
    const insideDist = Math.min(Math.max(dx, dy), 0);
    return outsideDist + insideDist;
  }

  /**
   * Compute distance to capsule axis segment.
   */
  private _distanceToCapsule(lx: number, ly: number, halfLength: number, radius: number, isVertical: boolean): number {
    // Capsule is aligned along Y (vertical) or X (horizontal)
    let axialDist: number;
    let perpDist: number;
    
    if (isVertical) {
      // Clamp to segment
      const clampedY = Math.max(-halfLength, Math.min(halfLength, ly));
      axialDist = Math.abs(ly) - halfLength;
      perpDist = Math.abs(lx);
      
      if (axialDist <= 0) {
        // Inside capsule length, just use perpendicular distance
        return perpDist - radius;
      } else {
        // Outside capsule ends, use distance to hemispherical caps
        return Math.sqrt(axialDist * axialDist + lx * lx) - radius;
      }
    } else {
      const clampedX = Math.max(-halfLength, Math.min(halfLength, lx));
      axialDist = Math.abs(lx) - halfLength;
      perpDist = Math.abs(ly);
      
      if (axialDist <= 0) {
        return perpDist - radius;
      } else {
        return Math.sqrt(axialDist * axialDist + ly * ly) - radius;
      }
    }
  }

  /**
   * Check if point is inside polygon and return normalized distance (0=center, 1=edge).
   */
  private _pointInPolygon(lx: number, ly: number, points: PhysVec2[]): { inside: boolean; normalizedDist: number } {
    if (points.length < 3) return { inside: false, normalizedDist: 1 };
    
    // Ray casting algorithm for point-in-polygon
    let inside = false;
    let minEdgeDist = Infinity;
    
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const xi = points[i].x, yi = points[i].y;
      const xj = points[j].x, yj = points[j].y;
      
      // Ray casting test
      if (((yi > ly) !== (yj > ly)) && (lx < (xj - xi) * (ly - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
      
      // Distance to edge segment
      const edgeX = xj - xi;
      const edgeY = yj - yi;
      const edgeLen = Math.sqrt(edgeX * edgeX + edgeY * edgeY);
      if (edgeLen > 0) {
        const t = Math.max(0, Math.min(1, ((lx - xi) * edgeX + (ly - yi) * edgeY) / (edgeLen * edgeLen)));
        const closestX = xi + t * edgeX;
        const closestY = yi + t * edgeY;
        const dist = Math.sqrt((lx - closestX) ** 2 + (ly - closestY) ** 2);
        minEdgeDist = Math.min(minEdgeDist, dist);
      }
    }
    
    // Compute centroid for normalized distance
    let cx = 0, cy = 0;
    for (const p of points) { cx += p.x; cy += p.y; }
    cx /= points.length;
    cy /= points.length;
    
    // Approximate max radius from centroid to vertices
    let maxRadius = 0;
    for (const p of points) {
      const d = Math.sqrt((p.x - cx) ** 2 + (p.y - cy) ** 2);
      maxRadius = Math.max(maxRadius, d);
    }
    
    // Distance from point to centroid
    const distToCenter = Math.sqrt((lx - cx) ** 2 + (ly - cy) ** 2);
    const normalizedDist = maxRadius > 0 ? distToCenter / maxRadius : 0;
    
    return { inside, normalizedDist: Math.min(normalizedDist, 1) };
  }

  /**
   * Compute zone containment info for a point.
   * Returns { inside, normalizedDist } where normalizedDist is 0 at center/full-strength, 1 at boundary.
   */
  private _computeZoneContainment(wx: number, wy: number, zone: GravityZoneState): { inside: boolean; normalizedDist: number; dirX: number; dirY: number } {
    const local = this._worldToZoneLocal(wx, wy, zone);
    const cfg = zone.config;
    
    // Direction from body to zone center (for Point gravity)
    const dx = zone.position.x - wx;
    const dy = zone.position.y - wy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const dirX = dist > 0.001 ? dx / dist : 0;
    const dirY = dist > 0.001 ? dy / dist : 0;
    
    switch (cfg.shape) {
      case GravityZoneShape.Circle: {
        const r = Math.sqrt(local.x ** 2 + local.y ** 2);
        if (r > cfg.outerRadius) return { inside: false, normalizedDist: 1, dirX, dirY };
        if (r <= cfg.innerRadius) return { inside: true, normalizedDist: 0, dirX, dirY };
        const t = (r - cfg.innerRadius) / (cfg.outerRadius - cfg.innerRadius);
        return { inside: true, normalizedDist: t, dirX, dirY };
      }
      
      case GravityZoneShape.Box: {
        const halfW = cfg.size.x * 0.5;
        const halfH = cfg.size.y * 0.5;
        const sdf = this._signedDistanceBox(local.x, local.y, halfW, halfH);
        if (sdf > 0) return { inside: false, normalizedDist: 1, dirX, dirY };
        // Map SDF to normalized distance (0 at center, 1 at edge)
        const maxInnerDist = Math.min(halfW, halfH);
        const normalizedDist = maxInnerDist > 0 ? 1 - Math.min(-sdf / maxInnerDist, 1) : 0;
        return { inside: true, normalizedDist, dirX, dirY };
      }
      
      case GravityZoneShape.Capsule: {
        const isVertical = cfg.direction === 0;
        const radius = cfg.outerRadius;
        const halfLength = isVertical ? (cfg.size.y * 0.5 - radius) : (cfg.size.x * 0.5 - radius);
        const sdf = this._distanceToCapsule(local.x, local.y, Math.max(0, halfLength), radius, isVertical);
        if (sdf > 0) return { inside: false, normalizedDist: 1, dirX, dirY };
        const normalizedDist = radius > 0 ? 1 - Math.min(-sdf / radius, 1) : 0;
        return { inside: true, normalizedDist, dirX, dirY };
      }
      
      case GravityZoneShape.Polygon: {
        const result = this._pointInPolygon(local.x, local.y, cfg.points);
        return { inside: result.inside, normalizedDist: result.normalizedDist, dirX, dirY };
      }
      
      default:
        return { inside: false, normalizedDist: 1, dirX, dirY };
    }
  }

  /**
   * Compute falloff weight based on normalized distance and zone config.
   */
  private _computeFalloff(normalizedDist: number, zone: GravityZoneState): number {
    const { falloff } = zone.config;
    
    if (normalizedDist <= 0) return 1;
    if (normalizedDist >= 1) return 0;
    
    const t = normalizedDist;
    
    switch (falloff) {
      case GravityFalloff.None:
        return 1;
      case GravityFalloff.Linear:
        return 1 - t;
      case GravityFalloff.InverseSquare:
        // Normalized inverse square: 1 at inner, 0 at outer
        return 1 / (1 + t * t * 3); // Adjusted to reach ~0.25 at t=1
      case GravityFalloff.Smoothstep:
        // Hermite interpolation (smooth start and end)
        return 1 - (t * t * (3 - 2 * t));
      default:
        return 1 - t;
    }
  }

  /**
   * Compute effective gravity for a body considering all gravity zones.
   */
  private _computeEffectiveGravity(body: PhysicsBodyState): PhysVec2 {
    let gx = this._gravity.x;
    let gy = this._gravity.y;
    
    // Track zone contributions for blending
    let totalWeight = 0;
    let blendGx = 0;
    let blendGy = 0;
    let hasOverride = false;
    let scaleAccum = 1;

    for (const zone of this._gravityZones) {
      if (!zone) continue;
      
      // Check layer mask
      if ((body.layerMask & zone.config.affectedLayers) === 0) continue;
      
      // Compute zone containment (shape-aware)
      const containment = this._computeZoneContainment(body.position.x, body.position.y, zone);
      
      // Skip if outside zone
      if (!containment.inside) continue;
      
      // Compute falloff weight
      const falloffWeight = this._computeFalloff(containment.normalizedDist, zone);
      if (falloffWeight <= 0) continue;
      
      // Weight by priority and falloff
      const weight = (zone.config.priority + 1) * falloffWeight;
      
      switch (zone.config.type) {
        case GravityZoneType.Override:
          // Override replaces global gravity
          blendGx += zone.config.gravity.x * weight;
          blendGy += zone.config.gravity.y * weight;
          totalWeight += weight;
          hasOverride = true;
          break;
          
        case GravityZoneType.Additive:
          // Additive adds to current gravity
          blendGx += zone.config.gravity.x * falloffWeight;
          blendGy += zone.config.gravity.y * falloffWeight;
          break;
          
        case GravityZoneType.Point:
          // Point gravity toward zone center
          const strength = zone.config.strength * falloffWeight;
          blendGx += containment.dirX * strength;
          blendGy += containment.dirY * strength;
          break;
          
        case GravityZoneType.Scale:
          // Scale multiplies current gravity
          scaleAccum *= 1 + (zone.config.strength - 1) * falloffWeight;
          break;
      }
    }

    // Apply results
    if (hasOverride && totalWeight > 0) {
      // Weighted average of override zones
      gx = blendGx / totalWeight;
      gy = blendGy / totalWeight;
    } else {
      // Apply scale to global gravity
      gx *= scaleAccum;
      gy *= scaleAccum;
    }
    
    // Add additive/point contributions
    if (!hasOverride) {
      gx += blendGx;
      gy += blendGy;
    }

    return { x: gx, y: gy };
  }

  /**
   * Apply effector forces (buoyancy, wind) to all dynamic bodies.
   */
  private _applyEffectorForces(dt: number): void {
    const hasBuoyancy = this._buoyancyZones.some(z => z);
    const hasWind = this._windZones.some(z => z);
    
    if (!hasBuoyancy && !hasWind) return;

    for (const body of this._bodies) {
      if (!body || body.bodyType !== BodyType.Dynamic) continue;
      if (body.sleepState === SleepState.Sleeping) continue;

      // Apply buoyancy zones
      if (hasBuoyancy) {
        this._applyBuoyancyToBody(body, dt);
      }

      // Apply wind zones
      if (hasWind) {
        this._applyWindToBody(body, dt);
      }
    }
  }

  /**
   * Apply buoyancy forces to a single body.
   */
  private _applyBuoyancyToBody(body: PhysicsBodyState, dt: number): void {
    for (const zone of this._buoyancyZones) {
      if (!zone) continue;
      
      // Check layer mask
      if ((body.layerMask & zone.affectedLayers) === 0) continue;
      
      // Check if body is inside zone (box shape)
      const inside = this._isInsideBuoyancyZone(body.position.x, body.position.y, zone);
      if (!inside.inside) continue;
      
      // Compute submerged fraction (0-1)
      const submergedFrac = inside.depth;
      
      // Buoyancy force using Archimedes' principle:
      // F_buoy = ρ_fluid * g * V_displaced
      // V_displaced = (mass / ρ_body) * submergedFrac
      // F_buoy = (ρ_fluid / ρ_body) * mass * g * submergedFrac
      // 
      // If ρ_fluid > ρ_body → body floats (buoyancy > weight)
      // If ρ_fluid < ρ_body → body sinks (buoyancy < weight)
      const bodyDensity = body.density > 0.001 ? body.density : 1;
      const buoyancyForce = (zone.density / bodyDensity) * body.mass * 9.81 * submergedFrac;
      
      const normalMag = Math.sqrt(zone.surfaceNormal.x ** 2 + zone.surfaceNormal.y ** 2);
      const nx = normalMag > 0.001 ? zone.surfaceNormal.x / normalMag : 0;
      const ny = normalMag > 0.001 ? zone.surfaceNormal.y / normalMag : 1;
      
      // Apply buoyancy (upward along surface normal)
      body.forceAccum.x += nx * buoyancyForce;
      body.forceAccum.y += ny * buoyancyForce;
      
      // Apply flow force (scaled by submerged fraction and body mass for more realistic effect)
      body.forceAccum.x += zone.flow.x * submergedFrac * body.mass;
      body.forceAccum.y += zone.flow.y * submergedFrac * body.mass;
      
      // Apply drag (reduces velocity)
      const dragFactor = 1 - Math.min(zone.linearDrag * dt * submergedFrac, 0.99);
      body.linearVelocity.x *= dragFactor;
      body.linearVelocity.y *= dragFactor;
      body.angularVelocity *= 1 - Math.min(zone.angularDrag * dt * submergedFrac, 0.99);
      
      this._wakeBody(body);
    }
  }

  /**
   * Check if point is inside buoyancy zone and compute depth.
   */
  private _isInsideBuoyancyZone(x: number, y: number, zone: BuoyancyZoneState): { inside: boolean; depth: number } {
    // Transform to local space
    const tx = x - zone.position.x;
    const ty = y - zone.position.y;
    
    const cos = Math.cos(-zone.rotation);
    const sin = Math.sin(-zone.rotation);
    const rx = tx * cos - ty * sin;
    const ry = tx * sin + ty * cos;
    
    const sx = zone.scale.x;
    const sy = zone.scale.y;
    const lx = sx !== 0 ? rx / sx : rx;
    const ly = sy !== 0 ? ry / sy : ry;
    
    // Box check
    const halfW = zone.width * 0.5;
    const halfH = zone.height * 0.5;
    
    if (Math.abs(lx) > halfW || Math.abs(ly) > halfH) {
      return { inside: false, depth: 0 };
    }
    
    // Compute depth as fraction from top to bottom
    // Surface normal defines "up", so depth increases opposite to normal
    const normalMag = Math.sqrt(zone.surfaceNormal.x ** 2 + zone.surfaceNormal.y ** 2);
    const nx = normalMag > 0.001 ? zone.surfaceNormal.x / normalMag : 0;
    const ny = normalMag > 0.001 ? zone.surfaceNormal.y / normalMag : 1;
    
    // Project position onto surface normal axis
    // Depth is 1 at bottom of zone, 0 at top
    const projectedY = -(lx * nx + ly * ny); // Negative because depth increases opposite to normal
    const depth = Math.max(0, Math.min(1, (projectedY + halfH) / zone.height));
    
    return { inside: true, depth };
  }

  /**
   * Apply wind forces to a single body.
   */
  private _applyWindToBody(body: PhysicsBodyState, dt: number): void {
    for (const zone of this._windZones) {
      if (!zone) continue;
      
      // Check layer mask
      if ((body.layerMask & zone.affectedLayers) === 0) continue;
      
      // Check if body is inside zone
      const inside = this._isInsideWindZone(body.position.x, body.position.y, zone);
      if (!inside.inside) continue;
      
      // Compute strength with falloff
      let strength = zone.strength * inside.falloffFactor;
      
      // Apply turbulence (random noise)
      if (zone.turbulence > 0) {
        const turbNoise = Math.sin(this._time * zone.turbulenceFrequency * 6.28 + body.position.x * 0.5) * 
                          Math.cos(this._time * zone.turbulenceFrequency * 4.17 + body.position.y * 0.7);
        strength *= 1 + zone.turbulence * turbNoise;
      }
      
      // Apply pulse (sinusoidal oscillation)
      if (zone.pulse > 0) {
        const pulseFactor = 1 + zone.pulse * Math.sin(this._time * zone.pulseFrequency * 6.28);
        strength *= pulseFactor;
      }
      
      // Compute wind direction
      let wx: number, wy: number;
      if (zone.mode === 0) {
        // Directional - constant direction
        const dirMag = Math.sqrt(zone.direction.x ** 2 + zone.direction.y ** 2);
        if (dirMag < 0.001) continue;
        wx = zone.direction.x / dirMag;
        wy = zone.direction.y / dirMag;
      } else {
        // Radial - from center outward
        const dx = body.position.x - zone.position.x;
        const dy = body.position.y - zone.position.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 0.001) continue;
        wx = dx / dist;
        wy = dy / dist;
      }
      
      // Apply wind force (F = strength * dt for impulse-like behavior)
      body.forceAccum.x += wx * strength;
      body.forceAccum.y += wy * strength;
      
      this._wakeBody(body);
    }
  }

  /**
   * Check if point is inside wind zone and compute falloff.
   */
  private _isInsideWindZone(x: number, y: number, zone: WindZoneState): { inside: boolean; falloffFactor: number } {
    // Transform to local space
    const tx = x - zone.position.x;
    const ty = y - zone.position.y;
    
    const cos = Math.cos(-zone.rotation);
    const sin = Math.sin(-zone.rotation);
    const rx = tx * cos - ty * sin;
    const ry = tx * sin + ty * cos;
    
    const sx = zone.scale.x;
    const sy = zone.scale.y;
    const lx = sx !== 0 ? rx / sx : rx;
    const ly = sy !== 0 ? ry / sy : ry;
    
    let inside = false;
    let normalizedDist = 0;
    
    if (zone.shape === 0) {
      // Circle
      const r = Math.sqrt(lx * lx + ly * ly);
      inside = r <= zone.radius;
      normalizedDist = zone.radius > 0 ? r / zone.radius : 0;
    } else {
      // Box
      const halfW = zone.width * 0.5;
      const halfH = zone.height * 0.5;
      inside = Math.abs(lx) <= halfW && Math.abs(ly) <= halfH;
      if (inside) {
        // Distance from center as fraction
        const fx = halfW > 0 ? Math.abs(lx) / halfW : 0;
        const fy = halfH > 0 ? Math.abs(ly) / halfH : 0;
        normalizedDist = Math.max(fx, fy);
      }
    }
    
    if (!inside) return { inside: false, falloffFactor: 0 };
    
    // Compute falloff
    let falloffFactor = 1;
    if (zone.falloff > 0 && normalizedDist > zone.falloffStart) {
      const t = (normalizedDist - zone.falloffStart) / (1 - zone.falloffStart);
      if (zone.falloff === 1) {
        // Linear
        falloffFactor = 1 - t;
      } else {
        // Smoothstep
        falloffFactor = 1 - (t * t * (3 - 2 * t));
      }
    }
    
    return { inside: true, falloffFactor };
  }

  private _integrateForces(dt: number): void {
    const globalGx = this._gravity.x;
    const globalGy = this._gravity.y;
    const hasZones = this._gravityZones.some(z => z);

    for (const body of this._bodies) {
      if (!body || body.bodyType !== BodyType.Dynamic) continue;
      if (body.sleepState === SleepState.Sleeping) continue;

      const constraints = body.constraints;
      const freezeX = (constraints & 1) !== 0;
      const freezeY = (constraints & 2) !== 0;
      const freezeRot = (constraints & 4) !== 0;

      // Compute effective gravity (with zones if present)
      let gx: number, gy: number;
      if (hasZones) {
        const g = this._computeEffectiveGravity(body);
        gx = g.x;
        gy = g.y;
      } else {
        gx = globalGx;
        gy = globalGy;
      }

      // Apply gravity and forces
      if (!freezeX) body.linearVelocity.x += (gx * body.gravityScale + body.forceAccum.x * body.invMass) * dt;
      if (!freezeY) body.linearVelocity.y += (gy * body.gravityScale + body.forceAccum.y * body.invMass) * dt;
      if (!freezeRot) body.angularVelocity += body.torqueAccum * body.invInertia * dt;

      // Damping
      body.linearVelocity.x *= 1 / (1 + dt * body.linearDamping);
      body.linearVelocity.y *= 1 / (1 + dt * body.linearDamping);
      body.angularVelocity *= 1 / (1 + dt * body.angularDamping);
    }
  }

  private _integratePositions(dt: number): void {
    const maxTrans = SOLVER_DEFAULTS.maxTranslation;
    const maxRot = SOLVER_DEFAULTS.maxRotation;

    for (const body of this._bodies) {
      if (!body || body.bodyType === BodyType.Static) continue;
      if (body.sleepState === SleepState.Sleeping) continue;

      // Clamp translation
      let dx = body.linearVelocity.x * dt;
      let dy = body.linearVelocity.y * dt;
      const translationSq = dx * dx + dy * dy;
      if (translationSq > maxTrans * maxTrans) {
        const scale = maxTrans / Math.sqrt(translationSq);
        dx *= scale;
        dy *= scale;
      }

      // Clamp rotation
      let dRot = body.angularVelocity * dt;
      if (dRot > maxRot) dRot = maxRot;
      if (dRot < -maxRot) dRot = -maxRot;

      const constraints = body.constraints;
      if ((constraints & 1) === 0) body.position.x += dx;
      if ((constraints & 2) === 0) body.position.y += dy;
      if ((constraints & 4) === 0) body.rotation += dRot;
    }
  }

  private _clampVelocities(): void {
    const maxLin = SOLVER_DEFAULTS.maxLinearVelocity;
    const maxAng = SOLVER_DEFAULTS.maxAngularVelocity;

    for (const body of this._bodies) {
      if (!body) continue;
      const vSq = body.linearVelocity.x * body.linearVelocity.x +
                   body.linearVelocity.y * body.linearVelocity.y;
      if (vSq > maxLin * maxLin) {
        const scale = maxLin / Math.sqrt(vSq);
        body.linearVelocity.x *= scale;
        body.linearVelocity.y *= scale;
      }
      if (body.angularVelocity > maxAng) body.angularVelocity = maxAng;
      if (body.angularVelocity < -maxAng) body.angularVelocity = -maxAng;
    }
  }

  // ==========================================================================
  // AABB UPDATE
  // ==========================================================================

  private _updateAABBs(): void {
    for (const col of this._colliders) {
      if (!col) continue;
      const body = this._bodies[col.bodyIndex];
      if (!body) continue;

      const aabb = computeColliderAABB(col, body.position, body.rotation);
      col.aabbMin.x = aabb.min.x;
      col.aabbMin.y = aabb.min.y;
      col.aabbMax.x = aabb.max.x;
      col.aabbMax.y = aabb.max.y;
    }
  }

  // ==========================================================================
  // SLEEP
  // ==========================================================================

  private _updateSleep(dt: number): void {
    const linThresh = SOLVER_DEFAULTS.linearSleepThreshold;
    const angThresh = SOLVER_DEFAULTS.angularSleepThreshold;
    const timeToSleep = SOLVER_DEFAULTS.timeToSleep;

    for (const body of this._bodies) {
      if (!body || body.bodyType !== BodyType.Dynamic) continue;

      const vSq = body.linearVelocity.x * body.linearVelocity.x +
                   body.linearVelocity.y * body.linearVelocity.y;
      const aVSq = body.angularVelocity * body.angularVelocity;

      if (vSq > linThresh * linThresh || aVSq > angThresh * angThresh) {
        body.sleepTimer = 0;
        body.sleepState = SleepState.Awake;
      } else {
        body.sleepTimer += dt;
        if (body.sleepTimer >= timeToSleep) {
          body.sleepState = SleepState.Sleeping;
          body.linearVelocity.x = 0;
          body.linearVelocity.y = 0;
          body.angularVelocity = 0;
        }
      }
    }
  }

  private _wakeBody(body: PhysicsBodyState): void {
    if (body.sleepState === SleepState.Sleeping) {
      body.sleepState = SleepState.Awake;
      body.sleepTimer = 0;
    }
  }

  // ==========================================================================
  // MASS COMPUTATION
  // ==========================================================================

  private _recomputeBodyMass(bodyIndex: number): void {
    const body = this._bodies[bodyIndex];
    if (!body || body.bodyType !== BodyType.Dynamic) return;

    let totalMass = 0;
    let totalInertia = 0;
    let totalArea = 0;
    let comX = 0, comY = 0;

    for (const col of this._colliders) {
      if (!col || col.bodyIndex !== bodyIndex || col.isTrigger) continue;

      const { mass, inertia, center, area } = this._computeColliderMass(col);
      totalMass += mass;
      totalInertia += inertia;
      totalArea += area;
      comX += center.x * mass;
      comY += center.y * mass;
    }

    if (totalMass > 0) {
      body.mass = totalMass;
      body.invMass = 1 / totalMass;
      body.centerOfMass.x = comX / totalMass;
      body.centerOfMass.y = comY / totalMass;
      body.inertia = totalInertia;
      body.invInertia = totalInertia > 0 ? 1 / totalInertia : 0;
      body.density = totalArea > 0 ? totalMass / totalArea : 1;
    } else {
      body.mass = 1;
      body.invMass = 1;
      body.inertia = 1;
      body.invInertia = 1;
      body.centerOfMass.x = 0;
      body.centerOfMass.y = 0;
      body.density = 1;
    }
  }

  private _computeColliderMass(col: ColliderState): MassData {
    const density = col.material.density;
    switch (col.type) {
      case ColliderType.Circle: {
        const r2 = col.radius * col.radius;
        const area = Math.PI * r2;
        const mass = density * area;
        const inertia = 0.5 * mass * r2;
        return { mass, invMass: mass > 0 ? 1 / mass : 0, inertia, invInertia: inertia > 0 ? 1 / inertia : 0, center: { ...col.offset }, area };
      }
      case ColliderType.Box: {
        const w = col.halfExtents.x * 2;
        const h = col.halfExtents.y * 2;
        const area = w * h;
        const mass = density * area;
        const inertia = (mass / 12) * (w * w + h * h);
        return { mass, invMass: mass > 0 ? 1 / mass : 0, inertia, invInertia: inertia > 0 ? 1 / inertia : 0, center: { ...col.offset }, area };
      }
      case ColliderType.Capsule: {
        const r = col.radius;
        const seg = col.capsuleHeight - r * 2;
        const area = seg * r * 2 + Math.PI * r * r;
        const mass = density * area;
        const inertia = mass * (col.capsuleHeight * col.capsuleHeight + col.radius * col.radius) / 12;
        return { mass, invMass: mass > 0 ? 1 / mass : 0, inertia, invInertia: inertia > 0 ? 1 / inertia : 0, center: { ...col.offset }, area };
      }
      default: {
        return { mass: 1, invMass: 1, inertia: 1, invInertia: 1, center: { x: 0, y: 0 }, area: 1 };
      }
    }
  }

  // ==========================================================================
  // EVENTS
  // ==========================================================================

  private _fireCollisionEvents(manifolds: CollisionManifold[]): void {
    const currentPairs = new Set<string>();

    for (const m of manifolds) {
      const key = `${m.bodyIndexA}:${m.bodyIndexB}`;
      currentPairs.add(key);

      const bodyA = this._bodies[m.bodyIndexA];
      const bodyB = this._bodies[m.bodyIndexB];
      if (!bodyA || !bodyB) continue;

      const event: CollisionEvent2D = {
        otherGuid: '',
        otherBodyIndex: 0,
        manifold: m,
        relativeVelocity: {
          x: bodyB.linearVelocity.x - bodyA.linearVelocity.x,
          y: bodyB.linearVelocity.y - bodyA.linearVelocity.y,
        },
      };

      const handlersA = this._eventHandlers.get(m.bodyIndexA);
      const handlersB = this._eventHandlers.get(m.bodyIndexB);

      if (this._activeContacts.has(key)) {
        // Stay
        if (handlersA?.onCollisionStay) {
          handlersA.onCollisionStay({ ...event, otherGuid: bodyB.guid, otherBodyIndex: m.bodyIndexB });
        }
        if (handlersB?.onCollisionStay) {
          handlersB.onCollisionStay({ ...event, otherGuid: bodyA.guid, otherBodyIndex: m.bodyIndexA });
        }
      } else {
        // Enter
        if (handlersA?.onCollisionEnter) {
          handlersA.onCollisionEnter({ ...event, otherGuid: bodyB.guid, otherBodyIndex: m.bodyIndexB });
        }
        if (handlersB?.onCollisionEnter) {
          handlersB.onCollisionEnter({ ...event, otherGuid: bodyA.guid, otherBodyIndex: m.bodyIndexA });
        }
      }

      this._activeContacts.set(key, m);
    }

    // Exit events
    for (const [key] of this._activeContacts) {
      if (!currentPairs.has(key)) {
        const [aIdx, bIdx] = key.split(':').map(Number);
        const bodyA = this._bodies[aIdx];
        const bodyB = this._bodies[bIdx];
        const handlersA = this._eventHandlers.get(aIdx);
        const handlersB = this._eventHandlers.get(bIdx);
        const manifold = this._activeContacts.get(key)!;

        if (handlersA?.onCollisionExit && bodyB) {
          handlersA.onCollisionExit({
            otherGuid: bodyB.guid,
            otherBodyIndex: bIdx,
            manifold,
            relativeVelocity: { x: 0, y: 0 },
          });
        }
        if (handlersB?.onCollisionExit && bodyA) {
          handlersB.onCollisionExit({
            otherGuid: bodyA.guid,
            otherBodyIndex: aIdx,
            manifold,
            relativeVelocity: { x: 0, y: 0 },
          });
        }

        this._activeContacts.delete(key);
      }
    }
  }

  private _fireTriggerEvents(triggerPairs: { a: ColliderState; b: ColliderState }[]): void {
    const currentTriggers = new Set<string>();

    for (const { a, b } of triggerPairs) {
      const key = `${a.bodyIndex}:${b.bodyIndex}`;
      currentTriggers.add(key);

      const bodyA = this._bodies[a.bodyIndex];
      const bodyB = this._bodies[b.bodyIndex];
      if (!bodyA || !bodyB) continue;

      const handlersA = this._eventHandlers.get(a.bodyIndex);
      const handlersB = this._eventHandlers.get(b.bodyIndex);

      if (this._activeTriggers.has(key)) {
        if (handlersA?.onTriggerStay) handlersA.onTriggerStay({ otherGuid: bodyB.guid, otherBodyIndex: b.bodyIndex });
        if (handlersB?.onTriggerStay) handlersB.onTriggerStay({ otherGuid: bodyA.guid, otherBodyIndex: a.bodyIndex });
      } else {
        if (handlersA?.onTriggerEnter) handlersA.onTriggerEnter({ otherGuid: bodyB.guid, otherBodyIndex: b.bodyIndex });
        if (handlersB?.onTriggerEnter) handlersB.onTriggerEnter({ otherGuid: bodyA.guid, otherBodyIndex: a.bodyIndex });
      }

      this._activeTriggers.add(key);
    }

    // Exit
    for (const key of this._activeTriggers) {
      if (!currentTriggers.has(key)) {
        const [aIdx, bIdx] = key.split(':').map(Number);
        const bodyA = this._bodies[aIdx];
        const bodyB = this._bodies[bIdx];
        const handlersA = this._eventHandlers.get(aIdx);
        const handlersB = this._eventHandlers.get(bIdx);

        if (handlersA?.onTriggerExit && bodyB) handlersA.onTriggerExit({ otherGuid: bodyB.guid, otherBodyIndex: bIdx });
        if (handlersB?.onTriggerExit && bodyA) handlersB.onTriggerExit({ otherGuid: bodyA.guid, otherBodyIndex: aIdx });

        this._activeTriggers.delete(key);
      }
    }
  }

  // ==========================================================================
  // CLEANUP
  // ==========================================================================

  clear(): void {
    this._bodies.length = 0;
    this._colliders.length = 0;
    this._bodyMap.clear();
    this._activeContacts.clear();
    this._activeTriggers.clear();
    this._eventHandlers.clear();
    this._broadPhase.clear();
    this._nextBodyIndex = 0;
    this._nextColliderIndex = 0;
    this._accumulator = 0;
    this._stepCount = 0;
  }
}
